import React, {
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
  useRef,
} from 'react'
import ExperienceCKEditor from './ExperienceCKEditor'
import {
  useErrorService,
  useNotificationService,
  ABSpinner,
} from '@abroad/components'
import API from '../../utils/API'
import {
  isWelcomeEmailComplete,
  sanitizeEmailHtmlLinks,
  validateWelcomeEmailBody,
  WELCOME_EMAIL_INSERT_VARS,
} from './enrollmentValidation'
import { reportExperienceFormDirty } from './questAutosave'

const QuestWelcomeEmail = forwardRef(
  ({ questDetail, onCompletenessChange, onSaveStateChange }, ref) => {
    const [editableContent, setEditableContent] = useState('')
    const [fieldError, setFieldError] = useState('')
    const [isLoading, setIsLoading] = useState(true)
    const [updateLoading, setUpdateLoading] = useState(false)
    const questId = questDetail?._id
    const Error = useErrorService()
    const Notification = useNotificationService()
    const editorRef = useRef(null)
    const contentRef = useRef('')
    const lastSavedContentRef = useRef('')
    // Kept for future diffing / preview against the last saved full template.
    // eslint-disable-next-line no-unused-vars
    const [fullHtmlTemplate, setFullHtmlTemplate] = useState('')

    const reportSaveState = (state) => {
      if (typeof onSaveStateChange === 'function') {
        onSaveStateChange(state)
      }
    }

    const reportCompleteness = (html) => {
      if (typeof onCompletenessChange !== 'function') return
      const complete = isWelcomeEmailComplete(html)
      onCompletenessChange({
        welcomeEmailHtml: complete ? html : '',
        emailTemplate: complete ? html : '',
      })
    }

    useEffect(() => {
      const getQuestDetail = async () => {
        try {
          const { data } = await API.AdminAPI.quests.getQuestWelcomeEmail(
            questId,
          )
          const emailTemplate = data?.emailTemplate || ''
          setFullHtmlTemplate(emailTemplate)

          const parser = new DOMParser()
          const doc = parser.parseFromString(emailTemplate, 'text/html')

          let bodyContent = ''
          if (doc.body) {
            const firstBoxPadding = doc.body.querySelector('.first-box-padding')
            if (firstBoxPadding) {
              bodyContent = firstBoxPadding.innerHTML
            } else {
              bodyContent = doc.body.innerHTML
            }
          } else {
            bodyContent = emailTemplate
          }

          bodyContent = bodyContent.replace(
            /\{\{\s*button\s*\|\s*safe\s*\}\}/g,
            '{{ button }}',
          )

          bodyContent = bodyContent.replace(
            /<div>\s*<a href="\{\{ link \}\}" class="btn">\s*ENROLL\s*<\/a>\s*<\/div>/g,
            '{{ button }}',
          )

          contentRef.current = bodyContent
          lastSavedContentRef.current = bodyContent
          reportExperienceFormDirty('enrol-email', false)
          setEditableContent(bodyContent)
          const loadedValidation = validateWelcomeEmailBody(bodyContent)
          setFieldError(loadedValidation.ok ? '' : loadedValidation.error || '')
          reportCompleteness(bodyContent)
          setIsLoading(false)
        } catch (error) {
          Error.showError(error)
          setIsLoading(false)
        }
      }

      if (questId) {
        getQuestDetail()
      }
      // Error from useErrorService() is a new object every render — do not
      // list it as a dependency or this effect loops forever.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [questId])

    useEffect(() => () => reportExperienceFormDirty('enrol-email', false), [])

    const reportContentDirty = (content) => {
      reportExperienceFormDirty(
        'enrol-email',
        content !== lastSavedContentRef.current,
      )
    }

    const insertVariable = (token) => {
      const editor = editorRef.current
      if (!editor) return
      editor.model.change((writer) => {
        const selection = editor.model.document.selection
        const position = selection.getFirstPosition()
        if (!position) return
        writer.insertText(token, position)
      })
      editor.editing.view.focus()
      const data = editor.getData()
      contentRef.current = data
      setEditableContent(data)
      const validation = validateWelcomeEmailBody(data)
      setFieldError(validation.ok ? '' : validation.error || '')
      reportCompleteness(data)
      reportContentDirty(data)
    }

    const updateEmail = async ({ silent = false, content } = {}) => {
      if (updateLoading) {
        return false
      }
      const sourceContent = content ?? contentRef.current ?? editableContent
      const validation = validateWelcomeEmailBody(sourceContent)
      setFieldError(validation.ok ? '' : validation.error || '')
      reportCompleteness(sourceContent)
      if (!validation.ok) {
        if (!silent) {
          Notification.showNotification(validation.error, 'danger')
        }
        return false
      }

      setUpdateLoading(true)
      reportSaveState('saving')
      try {
        let processedContent = sanitizeEmailHtmlLinks(sourceContent)

        processedContent = processedContent.replace(
          /\{\{\s*button\s*\}\}/gi,
          () => {
            return `<div><a href="{{ link }}" class="btn">ENROLL</a></div>`
          },
        )

        processedContent = processedContent
          .replace(/<figure class="image"><img ([^>]+)><\/figure>/g, '<img $1>')
          .replace(/<\/?figure[^>]*>/g, '')

        processedContent = processedContent.replace(
          /<p><a href="\{\{ link \}\}">ENROLL[^<]*<\/a><br><br><\/p>/g,
          '',
        )

        const wrapper = document.createElement('div')
        wrapper.innerHTML = processedContent

        const applyClasses = () => {
          const paragraphs = wrapper.querySelectorAll('p')
          paragraphs.forEach((p) => {
            p.classList.add('p-16', 'font-opensans', 'fw-400')
          })

          const lists = wrapper.querySelectorAll('ol, ul')
          lists.forEach((list) => {
            list.classList.add('p-16', 'font-opensans', 'fw-400')
            list.style.marginLeft = '1.5rem'
          })

          const anchors = wrapper.querySelectorAll('a')
          anchors.forEach((a) => {
            const anchorText = a.textContent.trim().toLowerCase()
            if (anchorText === 'enroll') {
              a.classList.add('btn')
            } else {
              a.classList.add('link-color')
            }
          })
        }

        applyClasses()
        processedContent = wrapper.innerHTML

        const finalHtmlTemplate = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>You are Invited to Join an Abroad Quest</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;500;700&display=swap');
    @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600&display=swap');

    .font-opensans {
      font-family: Open Sans, sans-serif !important;
    }
    .font-montserrat {
      font-family: Montserrat, sans-serif !important;
    }
    .im {
      color: initial !important;
    }
    div.im {
      color: initial !important;
    }
    html, body, span, h1, h2, h3, h4, h5, h6, p {
      margin: 0;
      padding: 0;
      border: 0;
      outline: 0;
      vertical-align: baseline;
      background: transparent;
    }
    .container {
      max-width: 680px;
      width: 100%;
      min-width: 320px;
      margin: 0 auto;
    }
    .main_logo {
      text-align: center;
      background: #0a223c;
      padding: 1.25rem;
    }
    .main_logo img {
      width: 80px;
      height: 48.28px;
      margin: auto;
    }
    .banner img {
      max-width: 100%;
    }
    .first-box-padding {
      padding: 1.25rem 2.5rem 3.125rem 2.5rem;
    }
    .last-box-padding {
      padding: 1.313rem 2.5rem 1.313rem 2.5rem;
    }
    .fw-500 {
      font-weight: 500;
    }
    .fw-400 {
      font-weight: 400;
    }
    .cursor-pointer {
      cursor: pointer;
    }
    .p-24 {
      font-size: 1.5rem;
      font-style: normal;
      font-weight: 600;
      line-height: 2.375rem;
      letter-spacing: -0.03rem;
      color: #000000;
    }
    .p-20 {
      color: #000;
      font-size: 1.25rem;
      font-style: normal;
      font-weight: 700;
      line-height: 1.625rem;
      letter-spacing: -0.025rem;
    }
    .p-16 {
      font-size: 1rem;
      font-style: normal;
      line-height: 1.625rem;
      letter-spacing: -0.02rem;
    }
    .p-12 {
      font-size: 0.75rem;
      font-style: normal;
      font-weight: 500;
      line-height: 1.375rem;
      color: #000000;
      letter-spacing: -0.015rem;
    }
    .p-12 a {
      color: #0a223c;
      cursor: pointer;
    }
    .mb-16px {
      margin-bottom: 1rem;
    }
    .p-10 {
      font-size: 0.625rem;
      line-height: 1.375rem;
      letter-spacing: -0.013rem;
      font-style: normal;
    }
    .border-top {
      border-top: 1px solid #bf9000;
      background-color: #f3f6f6;
    }
    .border-b {
      border-bottom: 1.5px solid #0a223c;
    }
    .link-color {
      color: #0a223c;
    }
    .mt-50px {
      margin-top: 3.125rem;
    }
    .btn {
      font-family: Montserrat, sans-serif !important;
      background: #0a223c;
      display: inline-block;
      color: #fff !important;
      padding: 0.938rem 2rem 0.938rem 2rem;
      font-size: 0.75rem;
      line-height: 0.75rem;
      text-align: center;
      border-radius: 42px;
      letter-spacing: 0.125rem;
      font-weight: 600;
      text-decoration: none;
    }
    .btn:hover {
      background: #1a3a5c;
    }
    .mt-30px {
      margin-top: 1.875rem;
    }
    .mb-30px {
      margin-bottom: 1.875rem;
    }
    .quest-highlight {
      background: #f8f9fa;
      padding: 1.5rem;
      border-radius: 8px;
      border-left: 4px solid #bf9000;
      margin: 1.5rem 0;
    }
    @media screen and (max-width: 768px) {
      .first-box-padding {
        padding: 1.25rem 1.5rem 3.125rem 1.5rem;
      }
      .last-box-padding {
        padding: 1.313rem 1.5rem 1.313rem 1.5rem;
      }
    }
  </style>
</head>
<body>
  <div class="container">
    <div class="main_logo">
      <img src="https://abroad-public.s3.us-east-2.amazonaws.com/mail/abroad-logo.png" alt="abroad-logo" />
    </div>
    <div class="banner">
      <img src="https://abroad-public.s3.us-east-2.amazonaws.com/mail/header-image.jpg" alt="abroad-banner" />
    </div>
    <div class="first-box-padding">
      ${processedContent}
    </div>
    <div class="border-top last-box-padding">
      <p class="p-12 font-opensans mb-16px">
        If you have any questions, feel free to contact us at
        <a href="mailto:experiences@abroad.io" class="link-color">experiences@abroad.io</a>
      </p>
      <p class="p-10 font-opensans">
        This email was sent to {{ email }}. You received this email because you were invited to participate in an Abroad Experience.
      </p>
    </div>
  </div>
</body>
</html>`

        const { status } = await API.AdminAPI.quests.updateQuestWelcomeEmail(
          questId,
          { emailTemplate: finalHtmlTemplate },
        )

        if (status) {
          setFullHtmlTemplate(finalHtmlTemplate)
          lastSavedContentRef.current = sourceContent
          reportExperienceFormDirty('enrol-email', false)
          reportCompleteness(sourceContent)
          reportSaveState('saved')
          if (!silent) {
            Notification.showNotification(
              'Welcome Email has been updated.',
              'success',
            )
          }
          return true
        }
        reportSaveState('error')
        return false
      } catch (e) {
        Error.showError(e)
        reportSaveState('error')
        return false
      } finally {
        setUpdateLoading(false)
      }
    }

    const sendTest = async () => {
      if (!questId || updateLoading) return false
      const sourceContent = contentRef.current ?? editableContent
      const validation = validateWelcomeEmailBody(sourceContent)
      setFieldError(validation.ok ? '' : validation.error || '')
      reportCompleteness(sourceContent)
      if (!validation.ok) {
        Notification.showNotification(
          validation.error || 'Fix the email body before sending a test.',
          'danger',
        )
        return false
      }

      if (sourceContent !== lastSavedContentRef.current) {
        const saved = await updateEmail({
          silent: true,
          content: sourceContent,
        })
        if (!saved) return false
      }

      try {
        const { status } = await API.AdminAPI.quests.sendQuestWelcomeEmailTest(
          questId,
        )
        if (status) {
          Notification.showNotification('Test email sent to you.', 'success')
          return true
        }
        return false
      } catch (e) {
        Error.showError(e)
        return false
      }
    }

    useImperativeHandle(ref, () => ({
      sendTest,
    }))

    const handleEditorChange = (event, editor) => {
      const data = editor.getData()
      contentRef.current = data
      setEditableContent(data)
      const validation = validateWelcomeEmailBody(data)
      setFieldError(validation.ok ? '' : validation.error || '')
      reportCompleteness(data)
      reportContentDirty(data)
    }

    if (isLoading) {
      return <ABSpinner />
    }

    return (
      <div className='experience-enrol-block'>
        <div className='experience-f'>
          <label className='experience-f-label'>Subject</label>
          <div
            className='experience-f-control'
            style={{ background: 'var(--wash)', color: 'var(--muted)' }}>
            {`Your place on ${
              questDetail?.name || questDetail?.title || 'this Quest'
            } is confirmed`}
          </div>
          <p className='experience-f-hint'>Set by the system. Not editable.</p>
        </div>

        <div className='experience-f mt-3'>
          <label className='experience-f-label'>Email body</label>
          <div className='experience-vars'>
            {WELCOME_EMAIL_INSERT_VARS.map((variable) => (
              <button
                key={variable.token}
                type='button'
                className='experience-tok'
                title={`Insert ${variable.token}`}
                onClick={() => insertVariable(variable.token)}>
                {variable.label}
              </button>
            ))}
          </div>
          <p className='experience-f-hint mt-0'>
            Keep these variables. {'{{ button }}'} is the signup link. A test
            uses this same body.
          </p>
          <ExperienceCKEditor
            name='emailTemplate'
            data={editableContent}
            className={fieldError ? 'has-error' : undefined}
            onReady={(editor) => {
              editorRef.current = editor || null
              if (editor) {
                editor.editing.view.change((writer) => {
                  writer.setStyle(
                    'height',
                    '50vh',
                    editor.editing.view.document.getRoot(),
                  )
                })
              }
            }}
            onChange={handleEditorChange}
            onBlur={(event, editor) => {
              const data = editor.getData()
              contentRef.current = data
              setEditableContent(data)
              updateEmail({ silent: true, content: data })
            }}
          />
          {fieldError ? (
            <p className='experience-f-error mt-2 mb-0' role='alert'>
              {fieldError}
            </p>
          ) : null}
        </div>
      </div>
    )
  },
)

QuestWelcomeEmail.displayName = 'QuestWelcomeEmail'

export default QuestWelcomeEmail
